import { Component, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexXAxis,
  ApexYAxis,
  ApexLegend,
  ApexTooltip,
  NgApexchartsModule,
} from "ng-apexcharts";
import { CoreService } from "../../../services/core.service";
import { ChartComponent } from "ng-apexcharts";

export type HeatmapChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  colors: string[];
  legend: ApexLegend;
  tooltip: ApexTooltip;
  grid: any;
  responsive?: any[];
};

@Component({
  selector: "app-social-ai-heatmap-chart",
  standalone: true,
  imports: [NgApexchartsModule, CommonModule],
  templateUrl: "./social-ai-heatmap-chart.component.html",
  styleUrls: ["./social-ai-heatmap-chart.component.scss"],
})
export class SocialAiHeatmapChartComponent implements OnChanges {
  @Input() selectedSiteId: number | null = null;
  @Input() selectedPlatformId: number | null = null;
  @Input() selectedPeriod: string | null = null;
  @Input() title: string = "";
  @Input() pages: any[] = [];
  @ViewChild("chartRef", { static: false })
  chart!: ChartComponent;

  companyID: number | null = null;

  isLoading = false;
  isMobile = window.innerWidth <= 768;

  days = ["Sunday", "Saturday", "Friday", "Thursday", "Wednesday", "Tuesday", "Monday"];
  hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));

  hasData = false;
  lastHeatmap: any = null;

  legendLevels = [
    { level: 0, label: "No Posts", color: "#f5f5fa" },
    { level: 1, label: "Level 1", color: "#c7d2fe" },
    { level: 2, label: "Level 2", color: "#60a5fa" },
    { level: 3, label: "Level 3", color: "#2563eb" },
    { level: 4, label: "Level 4", color: "#1e3a8a" },
  ];

  public chartOptions: HeatmapChartOptions = {
    series: [],
    chart: {
      type: "heatmap",
      height: this.isMobile ? 220 : 300,
      width: "100%",
      toolbar: { show: false },
    },
    // grid: {
    //   padding: { top: 5, right: 5, bottom: 5, left: 5 },
    //   borderColor: "#e6edf4",
    // },
    grid: {
      padding: { top: -20, right: -10, bottom: -20, left: -15 },
    },
    dataLabels: { enabled: false },
    colors: ["#2563eb"],
    plotOptions: {
      heatmap: {
        shadeIntensity: 0,
        enableShades: false,
        radius: 2,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: "#f5f5fa" },
            { from: 1, to: 1, color: "#c7d2fe" },
            { from: 2, to: 2, color: "#60a5fa" },
            { from: 3, to: 3, color: "#2563eb" },
            { from: 4, to: 4, color: "#1e3a8a" },
          ],
        },
      },
    },
    xaxis: {
      position: "top",
      categories: [],
      labels: {
        show: window.innerWidth > 768,
        style: { fontSize: "12px", colors: "#000" },
        offsetY: -4,
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        show: !this.isMobile,
        style: { fontSize: "12px", colors: "#000" },
        offsetX: -5,
      },
    },
    legend: { show: false },
    tooltip: {
      x: { show: false },
      y: {
        formatter: (val, opts) => {
          const day = this.days[opts.seriesIndex];
          const hour = this.hours[opts.dataPointIndex];
          const cell = this.lastHeatmap?.[day]?.[hour];
          const level = cell?.level ?? 0;
          if (level === 0) return "No posts";
          const er = cell?.er_median ?? "0.00";
          return `ER median: ${er} (Level ${level})`;
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 220 },
          xaxis: {
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            labels: { show: false }
          }
        }
      }
    ]
  };

  constructor(private core: CoreService) { }

  ngOnChanges(_: SimpleChanges): void {
    this.companyID = this.core.user?.companyID || 0;

    if (
      this.companyID &&
      this.selectedSiteId &&
      this.selectedPlatformId &&
      this.selectedPeriod
    ) {
      this.loadHeatmap();
    }
  }

  loadHeatmap() {
    this.isLoading = true;
    this.hasData = false;

    const body = {
      company_id: this.companyID,
      contentsite_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: this.selectedPeriod,
    };

    this.core.postData(`querymetrics/getheatmap/${this.companyID}`, body).subscribe({
      next: (res) => {
        if (!res?.heatmap) {
          this.setEmpty();
          return;
        }

        this.hasData = true;
        this.lastHeatmap = res.heatmap;

        this.chartOptions.xaxis = {
          ...this.chartOptions.xaxis,
          categories: this.hours,
        };

        this.chartOptions.series = this.days.map((day) => {
          const dayData = res.heatmap[day] ?? {};
          return {
            name: day.slice(0, 3),
            data: this.hours.map((h) => dayData[h]?.level ?? 0),
          };
        });
      },
      error: () => this.setEmpty(),
      complete: () => {
        this.isLoading = false;
      },
    });
  }


  extractLevel(seriesIndex: number, dataIndex: number) {
    try {
      const day = this.days[seriesIndex];
      const hour = this.hours[dataIndex];
      const cell = this.lastHeatmap?.[day]?.[hour];
      return cell?.level ?? 0;
    } catch {
      return 0;
    }
  }

  setEmpty() {
    this.hasData = false;
    this.isLoading = false;

    this.chartOptions.series = this.days.map((day) => ({
      name: day.slice(0, 3),
      data: this.hours.map(() => 0),
    }));
  }

  async exportChartImage(): Promise<string> {

    if (!this.chart) {
      throw new Error('Reach chart not ready');
    }

    const data = await this.chart.dataURI();
    return data.imgURI;
  }

}
